import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { RoleService } from '../services/role.service';
import { environment } from '../../environments/environment';
import { getPostLogoutRedirectUri, isRoleSwitchInProgress } from '../auth.config';
import { LoaderService } from '../services/loader-service/loader.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  /** In-flight silent MSAL token restore, shared across requests that race in before it resolves. */
  private pendingJwtRestore$: Observable<string | null> | null = null;

  constructor(
    public RoleService: RoleService,
    private readonly authService: MsalService,
    private readonly LoaderService: LoaderService
  ) {}
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const currentUser = sessionStorage.getItem('JwtToken');
    if (request.headers.has('x-skip-jwt')) {
      return this.forwardRequest(request, next);
    }
    if (currentUser) {
      return this.forwardRequest(this.withAuthHeader(request, currentUser), next);
    }
    // No JwtToken in this tab's sessionStorage — e.g. a link opened via "Open in new tab"
    // starts with an empty sessionStorage even though MSAL's own cache (localStorage-backed,
    // see auth.config.ts cacheLocation) still has the account, which previously meant every
    // request in the new tab 403'd until AppComponent's own async restore happened to finish
    // first. Restore synchronously here so the very first request in the tab waits for it.
    return this.restoreJwtTokenSilently().pipe(
      switchMap((token) =>
        this.forwardRequest(token ? this.withAuthHeader(request, token) : request, next)
      )
    );
  }

  private withAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /** Silently re-acquires the app JWT from MSAL's (cross-tab) cache and caches it in this
   * tab's sessionStorage. Shared across concurrent requests so a burst of calls on a fresh
   * tab triggers only one acquireTokenSilent round-trip. Resolves to null (never throws) when
   * there's no MSAL account to restore from or the silent acquisition fails — callers fall back
   * to the pre-existing unauthenticated-request behavior in that case. */
  private restoreJwtTokenSilently(): Observable<string | null> {
    if (this.pendingJwtRestore$) {
      return this.pendingJwtRestore$;
    }
    let account = null;
    try {
      account = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0] ?? null;
    } catch {
      account = null;
    }
    if (!account) {
      return of(null);
    }
    this.pendingJwtRestore$ = this.authService
      .acquireTokenSilent({
        account,
        scopes: [
          'https://' +
            environment.mslConfig.tenantName +
            '.onmicrosoft.com/' +
            environment.mslConfig.clientId +
            '/read',
        ],
      })
      .pipe(
        map((result) => {
          sessionStorage.setItem('JwtToken', result.accessToken);
          return result.accessToken;
        }),
        catchError(() => of(null)),
        shareReplay(1),
        finalize(() => {
          this.pendingJwtRestore$ = null;
        })
      );
    return this.pendingJwtRestore$;
  }

  private forwardRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Payload encryption lives in `CryptoInterceptor` (registered after this one, so
    // it has already decrypted the body by the time `catchError` below runs).
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Global user-friendly fallback for unhandled SBL API failures
        // (FISP-287). Covers both the advisor SBL flow and the client SBL view
        // — all hit `/api/sbl/...`. 401s keep their dedicated session handling
        // below; everything else 4xx/5xx surfaces a single friendly toast with
        // a reference id (when the backend provides one) for support tracing.
        if (
          error.status >= 400 &&
          error.status !== 401 &&
          this.isSblEndpoint(error?.url) &&
          !this.shouldSuppressSblErrorToast(request, error)
        ) {
          this.LoaderService.showSblErrorToast(this.extractReferenceId(error));
        }

        if (error.status === 401 && !error?.url?.includes("checkWealthboxPresent")) {
            if (isRoleSwitchInProgress()) {
              return throwError(() => error);
            }
            this.LoaderService.showToast();
            setTimeout(() => {
              if (isRoleSwitchInProgress()) {
                return;
              }
              if (localStorage.getItem("firmname")) {
                this.authService.logoutRedirect({
                  postLogoutRedirectUri: getPostLogoutRedirectUri()
                });
              }
              // NOTE: do NOT call authService.logout() — it queues a duplicate
              // logoutRedirect that races the explicit call above and may land on
              // the wrong firm. See advisor-sidebar.logout() diagnosis.
              sessionStorage.clear();
              const firmname = localStorage.getItem('firmname') || '';
              const roleType = localStorage.getItem('roleType') || '';
              localStorage.clear();
              if (firmname) { localStorage.setItem('firmname', firmname); }
              if (roleType) { localStorage.setItem('roleType', roleType); }
            }, 1000);
        }

        return throwError(() => error); // re-throw the error for further handling
      })
    );
  }

  /** True for SBL API requests (advisor flow + client SBL view share `/api/sbl/`). */
  private isSblEndpoint(url: string | null | undefined): boolean {
    return !!url && url.includes('/api/sbl/');
  }

  /**
   * Skip the global SBL error toast when the caller handles the failure locally.
   * `create-customer` (409 already exists, etc.) is non-blocking in the advisor wizard.
   */
  private shouldSuppressSblErrorToast(
    request: HttpRequest<any>,
    error: HttpErrorResponse
  ): boolean {
    if (request.headers.has('x-skip-sbl-error-toast')) {
      return true;
    }
    const url = String(error?.url ?? request.url ?? '');
    return url.includes('/api/sbl/loan-application/customer');
  }

  /**
   * Best-effort extraction of a support/trace reference id from an error
   * response. Handles object and JSON-string bodies and the common field
   * casings used across the backend. Returns `undefined` when none is present.
   */
  private extractReferenceId(error: HttpErrorResponse): string | undefined {
    let body: any = error?.error;
    if (typeof body === 'string') {
      const text = body.trim();
      if (!text) {
        return undefined;
      }
      try {
        body = JSON.parse(text);
      } catch {
        return undefined;
      }
    }
    if (!body || typeof body !== 'object') {
      return undefined;
    }
    const candidate =
      body.referenceId ??
      body.reference_id ??
      body.ReferenceId ??
      body.referenceID ??
      body.traceId ??
      body.trace_id ??
      body.correlationId ??
      body.correlation_id ??
      body.errorId ??
      body.error_id ??
      body.requestId ??
      body.request_id;
    const value = candidate == null ? '' : String(candidate).trim();
    return value || undefined;
  }
}
